// ===================================================
// hooks/useTariffName.js — узкий хук для шапки.
//
// Возвращает только название текущего тарифа компании, кешируя его в
// localStorage (TTL 60s). Это НЕ источник истины по тарифу/лимитам — для
// полного состояния используйте `useTariff` из `contexts/TariffContext`.
//
// До 2026-04-23 хук назывался `useTariff` и жил в `hooks/useTariff.js`,
// что конфликтовало с `useTariff` из контекста. Переименован в `useTariffName`
// в рамках Phase 0 UI Unification (см. FRONSYSTEM/promptplan/12_PLAN_UI_UNIFICATION_ALL_MODULES_2026.md).
// ===================================================
import { useState, useEffect, useRef } from 'react';
import axios from 'utils/axios';
import { billingApiPath } from 'utils/billingApiPaths';

const TARIFF_CACHE_KEY = 'mb_header_tariff_cache_v1';
const TARIFF_CACHE_TTL_MS = 60 * 1000;

const readTariffCache = (companyId) => {
  if (!companyId) return null;
  try {
    const raw = localStorage.getItem(TARIFF_CACHE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.companyId !== companyId) return null;
    if (!parsed.ts || (Date.now() - parsed.ts > TARIFF_CACHE_TTL_MS)) return null;
    return parsed;
  } catch (_) {
    return null;
  }
};

const writeTariffCache = (companyId, tariffName) => {
  if (!companyId || !tariffName) return;
  try {
    localStorage.setItem(TARIFF_CACHE_KEY, JSON.stringify({
      companyId,
      tariffName,
      ts: Date.now(),
    }));
  } catch (_) {
    // ignore localStorage failures
  }
};

/**
 * Получить и закешировать название текущего тарифа компании для шапки.
 *
 * @param {object|null} company — объект активной компании (например, из useAuth)
 * @returns {{ tariffName: string }}
 */
export const useTariffName = (company) => {
  const [tariffName, setTariffName] = useState('—');
  const companyRef = useRef(company);
  companyRef.current = company;

  useEffect(() => {
    const currentCompany = companyRef.current;
    const companyId = currentCompany?.id;
    if (!companyId) return;
    let cancelled = false;

    const fromCompany =
      currentCompany?.tariff_name || currentCompany?.tariff?.name || currentCompany?.plan || null;

    if (fromCompany) {
      setTariffName(fromCompany);
      writeTariffCache(companyId, fromCompany);
    } else {
      const cached = readTariffCache(companyId);
      if (cached?.tariffName) setTariffName(cached.tariffName);
    }

    (async () => {
      try {
        const resp = await axios.get(billingApiPath('current-tariff'));
        const fetched =
          resp?.data?.data?.name ||
          resp?.data?.data?.tariff_name ||
          resp?.data?.tariff?.name ||
          null;

        if (!cancelled && fetched) {
          setTariffName(fetched);
          writeTariffCache(companyId, fetched);
        }
      } catch (_) {
        // Тихо: при ошибке оставляем предыдущее значение (cache или прочерк).
      }
    })();

    return () => { cancelled = true; };
  }, [company?.id, company?.tariff_name, company?.plan]);

  return { tariffName };
};

export default useTariffName;
